import {
  TextField,
  Button,
  SelectField,
  ApiLoadingSpinner,
  ApiRequestError,
  SelectFieldOption,
} from "@funcional/react-ui";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { IProgramFamily } from "@pbm/programs-families/contracts";
import { Grid } from "@material-ui/core";
import { useRegistrationPolicies } from "@pbm/registration-policies/hooks";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

const containerSpacing = 3;

const schema = yup.object().shape({
  code: yup
    .string()
    .required()
    .min(3, "o código deve ter pelo menos 3 letras")
    .matches(
      /^[A-Z0-9_]*$/,
      "deve conter somente letras maiúsculas, números e sublinhado."
    ),
  name: yup.string().required().min(3, "o nome deve ter pelo menos 3 letras"),
  effectiveSince: yup.date().required(),
  effectiveUntil: yup
    .date()
    .min(
      yup.ref("effectiveSince"),
      "a data final não pode ser inferior à inicial"
    )
    .required(),
});

export default function ProgramFamilyForm({
  onSubmit,
  defaults,
  isLoading,
}: {
  onSubmit: SubmitHandler<FieldValues>;
  defaults?: IProgramFamily;
  isLoading?: boolean;
}) {
  const {
    status: registrationPoliciesStatus,
    data: registrationPoliciesData,
    error: registrationPoliciesError,
  } = useRegistrationPolicies();
  const form = useForm({
    resolver: yupResolver(schema),
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full max-w-xl">
      <Grid container spacing={containerSpacing}>
        <Grid item xs={12}>
          <TextField
            required
            name="code"
            label="Código"
            control={control}
            defaultValue={defaults?.code}
            helperText="Identificador alfanumérico único"
            readOnly={defaults && defaults.code ? true : false}
            errorMessage={errors?.code?.message}
            transform={{
              input: (value: any) =>
                value ? value.toUpperCase().replace(/[^A-Z0-9_]/g, "") : value,
              output: (e: any) =>
                e.target.value
                  ? e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "")
                  : e.target.value,
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            required
            name="name"
            label="Nome"
            control={control}
            defaultValue={defaults?.name}
            errorMessage={errors?.name?.message}
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            required
            control={control}
            type="datetime-local"
            id="effectiveSince"
            label="Início da vigência"
            defaultValue={
              defaults?.effectiveSince
                ? defaults.effectiveSince.split(".").shift()
                : undefined
            }
            errorMessage={errors?.effectiveSince?.message}
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            required
            control={control}
            type="datetime-local"
            id="effectiveUntil"
            label="Fim da vigência"
            defaultValue={
              defaults?.effectiveUntil
                ? defaults.effectiveUntil.split(".").shift()
                : undefined
            }
            errorMessage={errors?.effectiveUntil?.message}
          />
        </Grid>

        <Grid item xs={12}>
          {registrationPoliciesStatus === "loading" ? (
            <ApiLoadingSpinner />
          ) : registrationPoliciesStatus === "error" ? (
            <ApiRequestError error={registrationPoliciesError} />
          ) : (
            <SelectField
              control={control}
              defaultValue={
                defaults?.registrationPolicyId ||
                defaults?.registrationPolicy?.id ||
                ""
              }
              errorMessage={errors?.registrationPolicy?.message}
              name="registrationPolicyId"
              id="registrationPolicyId"
              label="Política cadastral"
            >
              <SelectFieldOption value={undefined}>-</SelectFieldOption>
              {registrationPoliciesData?.edges.map((item) => (
                <SelectFieldOption key={item.node.id} value={item.node.id}>
                  {item.node.description}
                </SelectFieldOption>
              ))}
            </SelectField>
          )}
        </Grid>

        <Grid item xs={12}>
          <Button disabled={isLoading} type="submit" color="primary">
            Salvar
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}
